// ─────────────────────────────────────────────────────────────────────────
// api.ts
// Client กลางสำหรับเรียก PHP REST API (แทนที่ localStorage/sessionStorage
// เดิมทั้งหมด) ทุกฟังก์ชัน return เป็น Promise — ใช้ได้ทั้งฝั่ง Admin
// Dashboard (ต้อง login, ใช้ cookie session) และฝั่งหน้าใบงานคนขับ (public)
//
// การตั้งค่า base URL:
//   - ระหว่างพัฒนา (`npm run dev`): ปล่อยเป็นค่าว่าง '' แล้วให้ vite.config.ts
//     proxy คำขอ /api และ /uploads ไปที่ PHP API container ให้ (ดู vite.config.ts)
//   - ตอน build จริงไปวางไว้ใน htdocs เดียวกับโฟลเดอร์ api/ (same-origin):
//     ก็ปล่อยเป็นค่าว่างเหมือนกัน ไม่ต้องแก้อะไร
//   - ถ้า deploy แยกโดเมน/พอร์ตกันจริง ๆ ค่อยตั้ง VITE_API_BASE_URL ใน .env
// ─────────────────────────────────────────────────────────────────────────

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; credentials?: boolean } = {},
): Promise<T> {
  const { method = 'GET', body, credentials = false } = options;
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: credentials ? 'include' : 'same-origin',
  });

  let data: any = null;
  try {
    data = await res.json();
  } catch {
    // response ไม่มี body (เช่น 204) — ปล่อยเป็น null
  }

  if (!res.ok) {
    const message = data?.message ?? data?.error ?? `คำขอไปยังเซิร์ฟเวอร์ล้มเหลว (HTTP ${res.status})`;
    throw new ApiError(message, res.status, data?.error);
  }
  return data as T;
}

// ── Auth (ต้องแนบ cookie session -> credentials: true) ─────────────────────
export interface ApiAdminSession {
  loggedIn: true;
  username: string;
  role: string;
  loginAt: string;
}

export const authApi = {
  login: (username: string, password: string) =>
    request<ApiAdminSession>('/api/auth/login.php', { method: 'POST', body: { username, password }, credentials: true }),
  logout: () => request<{ ok: true }>('/api/auth/logout.php', { method: 'POST', credentials: true }),
  me: () => request<ApiAdminSession>('/api/auth/me.php', { credentials: true }),
};

// ── Jobs (admin — ต้อง login) ────────────────────────────────────────────

/** ผลลัพธ์แบบแบ่งหน้า (server-side pagination) */
export interface JobsPage<T = any> {
  jobs: T[];
  total: number;
  page: number;
  limit: number;
}

/** สรุปตัวเลขการ์ดสถิติ + ใบงานล่าสุด (สำหรับหน้า Dashboard) */
export interface JobsSummary<T = any> {
  total: number;
  pending: number;
  in_progress: number;
  completed: number;
  transfer: number;
  daily: number;
  recent: T[];
}

export const jobsApi = {
  /** ⚠️ ดึงทั้งหมด — ใช้เท่าที่จำเป็น (งานเยอะจะช้า) เลี่ยงมาใช้ page() แทน */
  list: <T = any>() => request<T[]>('/api/jobs.php', { credentials: true }),

  /** ดึงใบงานทีละหน้า + กรอง/ค้นหาฝั่ง server (page เริ่มที่ 1) */
  page: <T = any>(params: {
    page?: number; limit?: number; type?: string; status?: string; q?: string;
    customer?: string; driver?: string; plate?: string; flight?: string;
  } = {}) => {
    const qs = new URLSearchParams();
    qs.set('page', String(params.page ?? 1));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.type && params.type !== 'all') qs.set('type', params.type);
    if (params.status && params.status !== 'all') qs.set('status', params.status);
    if (params.q) qs.set('q', params.q);
    for (const k of ['customer', 'driver', 'plate', 'flight'] as const) {
      if (params[k]) qs.set(k, params[k] as string);
    }
    return request<JobsPage<T>>(`/api/jobs.php?${qs.toString()}`, { credentials: true });
  },

  /** สรุปตัวเลข + ใบงานล่าสุด (เบา — ใช้กับการ์ดสถิติ/หน้า Dashboard) */
  summary: <T = any>() => request<JobsSummary<T>>('/api/jobs.php?summary=1', { credentials: true }),

  /** เหตุการณ์งานที่เพิ่งเริ่ม/จบ — ใช้แจ้งเตือนในเว็บ (in-app toast) */
  events: () => request<{ id: string; status: string; driverName: string | null; updatedAt: string }[]>(
    '/api/jobs.php?events=1', { credentials: true }),

  get: <T = any>(id: string) => request<T>(`/api/jobs.php?id=${encodeURIComponent(id)}`, { credentials: true }),
  create: <T = any>(job: T) => request<T>('/api/jobs.php', { method: 'POST', body: job, credentials: true }),
  update: <T = any>(id: string, partial: Partial<T>) =>
    request<T>(`/api/jobs.php?id=${encodeURIComponent(id)}`, { method: 'PATCH', body: partial, credentials: true }),
  remove: (id: string) =>
    request<{ ok: true }>(`/api/jobs.php?id=${encodeURIComponent(id)}`, { method: 'DELETE', credentials: true }),
  markPaid: <T = any>(id: string, amountPaid: number, slipPath: string) =>
    request<T>(`/api/jobs.php?id=${encodeURIComponent(id)}&action=mark_paid`, {
      method: 'POST',
      body: { amountPaid, slipPath },
      credentials: true,
    }),
};

// ── Job (public — หน้าใบงานคนขับ ไม่ต้อง login) ───────────────────────────
// เข้าถึงด้วย driver_token แบบสุ่ม (?token=...) แทน job id — id เดา/ไล่เลข
// ได้ง่ายเกินไปที่จะใช้เป็นกุญแจเข้าถึงตรง ๆ (ดู helpers.php::generate_driver_token())
export const jobPublicApi = {
  get: <T = any>(token: string) => request<T>(`/api/job_public.php?token=${encodeURIComponent(token)}`),
  update: <T = any>(token: string, partial: Partial<T>) =>
    request<T>(`/api/job_public.php?token=${encodeURIComponent(token)}`, { method: 'PATCH', body: partial }),
};

// ── Drivers (admin) ───────────────────────────────────────────────────────
export const driversApi = {
  list: <T = any>() => request<T[]>('/api/drivers.php', { credentials: true }),
};

// ── Tracking (admin) ──────────────────────────────────────────────────────
export interface TrackingSnapshot {
  status: 'pending' | 'in_progress' | 'completed';
  auditStartGps?: string | null;
  driverLocation: { lat: number; lng: number; updatedAt: string; accuracy?: number } | null;
  completionLocation: { lat: number; lng: number; savedAt: string; accuracy?: number } | null;
}

export const trackingApi = {
  get: (jobId: string) => request<TrackingSnapshot>(`/api/tracking.php?job_id=${encodeURIComponent(jobId)}`, { credentials: true }),
  saveCompletion: (jobId: string, completionLocation: NonNullable<TrackingSnapshot['completionLocation']>) =>
    request<TrackingSnapshot>(`/api/tracking.php?job_id=${encodeURIComponent(jobId)}`, {
      method: 'PATCH',
      body: { completionLocation },
      credentials: true,
    }),
};

// ── PromptPay QR (admin) ───────────────────────────────────────────────────
// ตรงกับ api/promptpay.php: GET /api/promptpay.php?target=XXX&amount=YYY
// -> { payload: string } ใช้วาดเป็น QR ฝั่ง frontend เอง (PromptPayQrModal.tsx)
export interface PromptPayPayloadResult {
  payload: string;
}

export const promptpayApi = {
  /**
   * @param target เบอร์พร้อมเพย์ (เบอร์โทร 10 หลัก) หรือเลขบัตรประชาชน/เลขผู้เสียภาษี (13 หลัก)
   * @param amount ยอดเงิน — ไม่ระบุ (undefined) จะได้ QR แบบ "ไม่ระบุยอด" ให้ผู้จ่ายกรอกเอง
   */
  getPayload: (target: string, amount?: number) => {
    const params = new URLSearchParams({ target });
    if (amount !== undefined) params.set('amount', String(amount));
    return request<PromptPayPayloadResult>(`/api/promptpay.php?${params.toString()}`, { credentials: true });
  },
};

// ── Web Push (public — ใช้ทั้งฝั่งคนขับ (?token=) และแอดมิน (credentials)) ──
export interface PushSubscriptionJSON {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

export const pushApi = {
  getVapidPublicKey: () => request<{ publicKey: string }>('/api/push_vapid_key.php'),
  subscribe: (subscription: PushSubscriptionJSON, token?: string) => {
    const qs = token ? `?token=${encodeURIComponent(token)}` : '';
    return request<{ ok: true }>(`/api/push_subscribe.php${qs}`, {
      method: 'POST',
      body: { subscription },
      credentials: !token, // แอดมินต้องแนบ cookie session, คนขับไม่ต้อง
    });
  },
  unsubscribe: (endpoint: string) =>
    request<{ ok: true }>('/api/push_subscribe.php', { method: 'DELETE', body: { endpoint } }),
};

// ── Upload (public — ใช้ทั้งฝั่งคนขับและแอดมิน) ────────────────────────────
export type UploadKind = 'signature' | 'delivery' | 'slip' | 'start';
export interface UploadResult {
  path: string; // เก็บลง DB ผ่าน jobsApi.update(...)
  url: string;  // ใช้แสดงผลได้ทันที เช่น <img src={url}>
  /** ผลตรวจสอบสลิปแบบ heuristic — มีเฉพาะตอน kind==='slip' เท่านั้น */
  slipAnalysis?: import('./job.types').SlipAnalysis;
}

export const uploadApi = {
  /**
   * อัปโหลดรูปภาพจาก base64 data URI (ผลลัพธ์ของ canvas.toDataURL()/FileReader)
   *
   * ต้องพิสูจน์สิทธิ์ก่อนอัปโหลด (ดู api/upload.php):
   *   • ฝั่งคนขับ (kind 'signature'|'delivery'|'start') — ส่ง opts.token
   *     (driver_token จาก ?token=... ของลิงก์ใบงาน)
   *   • ฝั่งแอดมิน (kind 'slip') — ไม่ต้องส่ง token, จะแนบ cookie session ให้เอง
   *
   * @param opts.claimedAmount ยอดเงินที่คาดว่าสลิปควรมี — ใช้ตอน kind==='slip'
   *   เพื่อให้ระบบเทียบกับยอดที่ OCR อ่านได้ (ไม่บังคับ)
   * @param opts.token driver_token ของใบงาน (บังคับสำหรับฝั่งคนขับ)
   */
  uploadDataUri: (
    jobId: string,
    kind: UploadKind,
    dataURI: string,
    opts: { claimedAmount?: number; token?: string | null } = {},
  ) => {
    const { claimedAmount, token } = opts;
    const qs = token ? `?token=${encodeURIComponent(token)}` : '';
    return request<UploadResult>(`/api/upload.php${qs}`, {
      method: 'POST',
      body: { jobId, kind, dataURI, ...(claimedAmount !== undefined ? { claimedAmount } : {}) },
      credentials: !token, // แอดมิน (ไม่มี token) แนบ cookie session; คนขับใช้ token
    });
  },
};
